import axios from 'axios';

// Customer session storage + silent token refresh.
//
// The access token (JWT, 7 days) or web Google/Apple session token (7 days) used to
// expire with no way to renew it, so the first 401 after a week logged the user out
// mid-task. Every login path now also stores a refresh token (JWT, 30 days, rotated
// on each refresh), and the global 401 interceptor in App.js calls refreshSession()
// before giving up on the session (see SHOOTZU_BIBLE §15 2026-10-15).

export const TOKEN_KEY = 'shootzu_token';
export const REFRESH_KEY = 'shootzu_refresh_token';
export const USER_KEY = 'shootzu_user';

const backendUrl = () => process.env.REACT_APP_BACKEND_URL || 'https://shootzu.com';

export function storeRefreshToken(refreshToken) {
  if (refreshToken) {
    localStorage.setItem(REFRESH_KEY, refreshToken);
  } else {
    localStorage.removeItem(REFRESH_KEY);
  }
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

let inflight = null;

// Exchange the stored refresh token for a new access token. A burst of 401s (a page
// firing several requests at once) shares a single refresh request.
// Resolves to { token } on success, or { error: 'suspended' | 'failed' }. Never rejects.
export function refreshSession() {
  if (inflight) return inflight;

  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return Promise.resolve({ error: 'failed' });

  inflight = axios
    .post(`${backendUrl()}/api/auth/refresh`, { refreshToken })
    .then((res) => {
      const { accessToken, refreshToken: nextRefreshToken } = res.data || {};
      if (!accessToken) return { error: 'failed' };
      localStorage.setItem(TOKEN_KEY, accessToken);
      if (nextRefreshToken) localStorage.setItem(REFRESH_KEY, nextRefreshToken);
      return { token: accessToken };
    })
    .catch((err) => ({ error: err.response?.status === 403 ? 'suspended' : 'failed' }))
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

// Only customer API calls made with a real session are refreshed. Auth endpoints are
// skipped (/auth/refresh especially, so a failed refresh can't recurse), as are admin
// endpoints (adminAxios has its own refresh) and admin impersonation tokens.
// /auth/me is NOT skipped: it's how the app validates the session on load.
export function isRefreshableRequest(config, token) {
  if (!config || !token || token.startsWith('admin_viewing_as_')) return false;
  const url = config.url || '';
  const isAuthEndpoint = ['/auth/login', '/auth/google', '/auth/apple', '/auth/refresh']
    .some((path) => url.includes(path));
  return !isAuthEndpoint && !url.includes('/api/admin/');
}

// The global axios response-error handler (installed in App.js). On a 401 from a
// refreshable request it refreshes once and replays the request; if the refresh fails,
// or the replay is still 401, it ends the session. Everything else passes through.
export function createUnauthorizedHandler({ client, endSession }) {
  return async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && isRefreshableRequest(original, localStorage.getItem(TOKEN_KEY))) {
      if (!original._retriedAfterRefresh) {
        const result = await refreshSession();
        if (result.token) {
          original._retriedAfterRefresh = true;
          return client(withRefreshedAuth(original, result.token));
        }
        console.log('Session expired and refresh failed - logging out user');
        endSession(result.error === 'suspended');
      } else {
        // Still 401 with a freshly refreshed token: the session really is gone.
        endSession(false);
      }
    }
    return Promise.reject(error);
  };
}

// Call sites build the Authorization header themselves from localStorage, so a request
// retried after a refresh still carries the expired token. Swap it for the new one.
export function withRefreshedAuth(config, token) {
  const headers = config.headers;
  if (!headers) return config;
  const hasAuth = typeof headers.get === 'function'
    ? !!headers.get('Authorization')
    : !!(headers.Authorization || headers.authorization);
  if (hasAuth) {
    if (typeof headers.set === 'function') {
      headers.set('Authorization', `Bearer ${token}`);
    } else {
      delete headers.authorization;
      headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
}
